import { useCallback, useEffect, useState } from "react";

import { FixedClickableTextPanel } from "@/Common/Components/ClickableText/FixedClickableTextPanel";
import { SimpleTextareaResizable } from "@/Common/Components/Inputs/SimpleTextareaResizable";
import { JSXHelper } from "@/Common/Helpers/JSXHelper";
import * as LocalPatientOperations from "@/Common/Operations/operations.patient.local";
import { Encounter } from "@/Patient/Models/MedicalSummary/Encounter";
import { EncounterNote } from "@/Patient/Models/MedicalSummary/EncounterNote";

import style from "./OtherPlans.module.scss";

interface IOtherPlansProps {
    isFollowUpTextVisible: boolean;
    isEditMode: boolean;
    isReadOnly: boolean;
    notesPlan: EncounterNote[];
    encounter: Encounter
    showNotePopup: (e: React.MouseEvent) => void;
    onCloseClickableText: () => void;
    showClickableVisibility: (e?: React.MouseEvent) => void;
    onAutosaveFieldBlur: () => void;
    toggleAutoSave: () => void;
}

function OtherPlans(props: IOtherPlansProps): JSX.Element {
    const canQuickSignDocuments = cacheStore.ClinicSettings.EnableQuickDocumentSigning && !props.isReadOnly;
    const [content, setContent] = useState<string>(props.encounter.OtherPlanNote);

    useEffect(() => {
        setContent(props.encounter.OtherPlanNote ?? "");
    }, [props.encounter.OtherPlanNote]);

    const onItemChanged = useCallback(value => {
        sessionStore.dispatch(LocalPatientOperations.MedicalNote.Edit_FollowupPlanChanged, { value });
        props.toggleAutoSave();
    }, [props.toggleAutoSave]);

    return (
        <tr className={style.otherPlansRow}>
            <td className="c1">
                <div className={canQuickSignDocuments ? "margin-md-top" : ""}>
                    <strong>{cacheStore.GetString("OtherPlanNoteLbl")}</strong>
                </div>
            </td>
            <td className="c2 bottom-pad">
                <div className="tb-row tb">
                    <div className={`add-btn-column ${canQuickSignDocuments ? "padding-md-top" : ""}`}>
                        {!props.isReadOnly && (
                            <a onClick={e => props.showNotePopup(e)} data-cy="medicalNote-add-followupPlan">
                                {isArrayNullOrUndefinedOrEmpty(props.notesPlan) ?
                                    <span>[+]</span> :
                                    <span>[<span className="red-text">&nbsp;<b>{props.notesPlan.length}</b>&nbsp;</span>]</span>
                                }
                            </a>
                        )}
                    </div>
                    <div className={`note-items-column medical-notes ${canQuickSignDocuments ? "padding-md-top" : ""}`}>
                        {!props.isEditMode && (
                            <div className="note-container" data-cy="medicalNote-followupPlan">
                                {!IsNullOrWhiteSpace(props.encounter.OtherPlanNote) &&
                                    JSXHelper.BreakStringIntoParagraphs(props.encounter.OtherPlanNote, true)
                                }
                            </div>
                        )}

                        {props.isEditMode && (
                            <div>
                                <SimpleTextareaResizable
                                    id={`note_otherplan_${props.encounter.Id}`}
                                    data-cy="medicalNote-followupPlan"
                                    value={content}
                                    onChange={e => {
                                        setContent(e.target.value);
                                        onItemChanged(e.target.value);
                                    }}
                                    onBlur={() => props.onAutosaveFieldBlur?.()}
                                    className={`vresize ${style.textArea}`}
                                    minHeight="54px"
                                    maxHeight="99999px"
                                />

                                <FixedClickableTextPanel
                                    dataCy="medicalNote-followupPlan"
                                    cssClass="text-helper-container medical-note-clickable-text"
                                    context="OTH"
                                    inputElementId={`note_otherplan_${props.encounter.Id}`}
                                    isLinkDisabled={false}
                                    isClickableTextPanelOpen={props.isFollowUpTextVisible}
                                    onClickableTextPanelOpen={() => props.showClickableVisibility()}
                                    onClickableTextPanelClose={() => props.onCloseClickableText()}
                                    textboxHeight={470}
                                />
                            </div>
                        )}
                    </div>
                </div>
            </td>
        </tr>
    );
}

export { OtherPlans };
